import React, { useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Typography,
  LinearProgress,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
} from "@material-ui/core";
import {
  AccessTime,
  CheckCircle,
  Assessment,
  Warning,
  Schedule,
} from "@material-ui/icons";
import { fNumber, fPercent } from "src/utils/formatNumber";
import useChart from "../charts/use-chart";
import Chart from "react-apexcharts";

export default function RendimientoEmpleado({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Procesar datos para el gráfico de distribución de estados
  const estadosData = useMemo(() => {
    if (!data?.distribucionEstados?.length) return { series: [], labels: [] };

    const sortedData = [...data.distribucionEstados].sort(
      (a, b) => b.cantidad - a.cantidad,
    );

    return {
      series: sortedData.map((item) => item.cantidad),
      labels: sortedData.map((item) => {
        const estadoLabels = {
          PENDIENTE: "Pendientes",
          "EN PROCESO": "En Proceso",
          TERMINADA: "Terminadas",
          FACTURADO: "Facturadas",
          "PENDIENTE DE FACTURACION": "Pend. Facturación",
          "CANCELADO O RECHAZADO": "Canceladas",
        };
        return estadoLabels[item.estado] || item.estado;
      }),
    };
  }, [data?.distribucionEstados]);

  // Configuración del gráfico de barras horizontales
  const barOptions = useChart({
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        barHeight: isMobile ? "70%" : "60%",
        distributed: true,
      },
    },
    colors: ["#2196f3", "#4caf50", "#ff9800", "#f44336", "#9c27b0", "#607d8b"],
    xaxis: {
      categories: estadosData.labels,
      labels: {
        style: {
          fontSize: isMobile ? "10px" : "12px",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: isMobile ? "10px" : "12px",
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: isMobile ? "10px" : "12px",
      },
    },
    tooltip: {
      y: {
        formatter: (value) => `${value} tareas`,
      },
    },
    legend: {
      show: false,
    },
  });

  const barSeries = [
    {
      name: "Tareas",
      data: estadosData.series,
    },
  ];

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Card>
    );
  }

  const {
    tiempoResolucion = {},
    cumplimientoTiempos = {},
    eficienciaGeneral = 0,
  } = data || {};

  const { promedioDias = 0, medianaDias = 0, p90Dias = 0 } = tiempoResolucion;
  const {
    aTiempo = 0,
    vencidas = 0,
    sinFechaLimite = 0,
    totales = {},
    porcentajes = {},
  } = cumplimientoTiempos;

  const { conFechaLimite = 0 } = totales;
  const { soloConFechaLimite = 0, global = 0 } = porcentajes;

  // Función para determinar el color basado en el rendimiento
  const getPerformanceColor = (percentage) => {
    if (percentage >= 80) return "#4caf50";
    if (percentage >= 60) return "#ff9800";
    return "#f44336";
  };

  const getTimeColor = (days) => {
    if (days <= 3) return "#4caf50";
    if (days <= 7) return "#ff9800";
    return "#f44336";
  };

  return (
    <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        gutterBottom
        fontWeight="bold"
      >
        Rendimiento y Eficiencia
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {/* Métricas de tiempo y eficiencia */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={2}>
            {/* Tiempo de Resolución */}
            <Box
              sx={{
                p: 2,
                bgcolor: "background.neutral",
                borderRadius: 2,
              }}
            >
              <Box display="flex" alignItems="center" mb={1.5}>
                <AccessTime sx={{ color: getTimeColor(promedioDias), mr: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  Tiempo de Resolución
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={4}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "h6" : "h5"}
                      sx={{ color: getTimeColor(promedioDias) }}
                      fontWeight="bold"
                    >
                      {fNumber(promedioDias)}d
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Promedio
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "body1" : "h6"}
                      color="textPrimary"
                      fontWeight="bold"
                    >
                      {fNumber(medianaDias)}d
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Mediana
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 1,
                      bgcolor: "background.paper",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "body1" : "h6"}
                      color="textPrimary"
                      fontWeight="bold"
                    >
                      {fNumber(p90Dias)}d
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      P90
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Cumplimiento de Tiempos */}
            <Box
              sx={{
                p: 2,
                bgcolor: "background.neutral",
                borderRadius: 2,
              }}
            >
              <Box display="flex" alignItems="center" mb={1.5}>
                <CheckCircle
                  sx={{ color: getPerformanceColor(global), mr: 1 }}
                />
                <Typography variant="subtitle2" fontWeight="bold">
                  Cumplimiento de Tiempos
                </Typography>
              </Box>

              {/* Eficiencia General */}
              <Box
                sx={{
                  textAlign: "center",
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  sx={{ color: getPerformanceColor(global) }}
                  fontWeight="bold"
                >
                  {fPercent(global)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Eficiencia Global
                </Typography>
              </Box>

              <Stack spacing={1}>
                {/* A tiempo */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ p: 1, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircle
                      sx={{ color: "#4caf50", fontSize: 16, mr: 0.5 }}
                    />
                    <Typography variant="caption">A Tiempo</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {fNumber(aTiempo)}
                  </Typography>
                </Box>

                {/* Vencidas */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ p: 1, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  <Box display="flex" alignItems="center">
                    <Warning sx={{ color: "#f44336", fontSize: 16, mr: 0.5 }} />
                    <Typography variant="caption">Vencidas</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {fNumber(vencidas)}
                  </Typography>
                </Box>

                {/* Sin fecha límite */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ p: 1, bgcolor: "background.paper", borderRadius: 1 }}
                >
                  <Box display="flex" alignItems="center">
                    <Schedule
                      sx={{ color: "#9e9e9e", fontSize: 16, mr: 0.5 }}
                    />
                    <Typography variant="caption">Sin Fecha</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {fNumber(sinFechaLimite)}
                  </Typography>
                </Box>

                {/* Total con fecha límite */}
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    bgcolor: "info.lighter",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "info.light",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight="medium"
                    >
                      Total con Fecha Límite
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="info.main"
                    >
                      {fNumber(conFechaLimite)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>

              {/* Porcentaje con fecha límite */}
              {soloConFechaLimite > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    mb={0.5}
                  >
                    Con Fecha Límite: {fPercent(soloConFechaLimite)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={soloConFechaLimite}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: "grey.200",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: getPerformanceColor(soloConFechaLimite),
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Eficiencia general (KPI) */}
            {eficienciaGeneral > 0 && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "primary.lighter",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={1}
                >
                  <Assessment sx={{ color: "primary.main", mr: 1 }} />
                  <Typography
                    variant="subtitle2"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    KPI General
                  </Typography>
                </Box>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  color="primary.main"
                  fontWeight="bold"
                >
                  {fPercent(eficienciaGeneral)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* Gráfico de distribución de estados */}
        <Grid item xs={12} lg={7}>
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              color="textSecondary"
              fontWeight="medium"
            >
              Distribución por Estado
            </Typography>
            {estadosData.series.length > 0 ? (
              <Box
                sx={{
                  height: { xs: 300, sm: 400, lg: "100%" },
                  minHeight: 350,
                }}
              >
                <Chart
                  options={barOptions}
                  series={barSeries}
                  type="bar"
                  height="100%"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.neutral",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No hay datos de estados disponibles
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}
